<script>
    import dressing from "@intellectability/platform-common";

    import {FlexList, Modal} from "@intellectability/platform-common/sv";
    import {ModalBody, ModalFooter} from "@sveltestrap/sveltestrap";

    import {fade} from "svelte/transition";
    import {getContext} from "svelte";

    const providerValues = getContext("providerValues");

    const statuses = $derived(providerValues?.statuses ?? []);

    /**
     * @typedef {object} Props
     * @property {boolean} modalOpen
     * @property {number} providerID
     * @property {Function} onproviderselected - callback function that returns the selected provider ID
     */

    /**
     * @type {Props}
     */
    let {modalOpen = $bindable(false), onproviderselected, providerID} = $props();

    let loading = $state(true);

    let userSecurity = getContext("userSecurity");
    let loggedIn = $derived(userSecurity?.loggedIn ?? false);

    let load = $derived(modalOpen && loggedIn && Number.isInteger(providerID));

    let providerFamily = $state.raw({
        parent: {},
        children: [],
        siblings: [],
        self: {}
    });

    let columns = [
        {
            name: "ID",
            field: "id"
        },
        {
            name: "Name",
            field: "name"
        },
        {
            name: "Address",
            field: "address"
        },
        {
            name: "City",
            field: "city"
        },
        {
            name: "State",
            field: "state"

        },
        {
            name: "Zip",
            field: "zip"
        },
        {
            name: "Status",
            field: "status",
            cell: function (row) {
                const rowStatus = Number(row.status);
                const found = statuses.find(status => Number(status.value) === rowStatus);
                if (found) {
                    return found.label;
                }
                return "Unknown";
            }
        }
    ];

    let list_props = {
        columns,
        rowCountColumn: false,
        showFooter: false,
        hoverable: true,
        onselection: (row) => {
            const selectedProviderID = Number(row.id);
            if (selectedProviderID !== providerID) {
                onproviderselected?.(Number(row.id));
            }
        }
    };

    $effect(function () {
        if (load) {
            loading = true;
            dressing.get(`providers/family/${providerID}`)
                .then(({data: res}) => {

                    const {success, data} = res;
                    if (success === true) {
                        providerFamily = data;
                    }

                    loading = false;
                });
        }
    });

    function toggle_modal() {
        modalOpen = !modalOpen;
    }
</script>

<Modal bind:showModal={modalOpen} title="Parent/Child Provider details">
    <ModalBody>
        <wa-callout class="mb-3 rounded-2" variant="success">
            <wa-icon slot="icon" name="people-group" variant="regular" family="duotone"></wa-icon>
            Provider to Provider Relationship details for <strong>{providerFamily.self.name}</strong>.
        </wa-callout>
        {#key loading}
            <div in:fade|global>
                <div class="mb-3">
                    <div class="d-flex align-items-center mb-2 justify-content-between">
                        <div class="">
                            <h4 class="mb-0">Parent Provider</h4>
                        </div>
                        <wa-icon class="help-icon" id="parent-help" name="circle-question" variant="solid"></wa-icon>
                        <wa-tooltip for="parent-help" placement="top-end">
                            Providers can have one parent which is selected from the child provider's record.
                        </wa-tooltip>
                    </div>
                    <FlexList defaultPageLength={1}
                              {loading}
                              data={[providerFamily.parent]} {...list_props}></FlexList>
                </div>
                <hr/>
                <div class="mb-4">
                    <div class="d-flex align-items-center mb-2 justify-content-between">
                        <div>
                            <h4 class="mb-0">Sibling Providers</h4>
                        </div>
                        <div>
                            <wa-icon class="help-icon" id="sibling-help" name="circle-question"
                                     variant="solid"></wa-icon>
                            <wa-tooltip for="sibling-help" placement="top-end">
                                Providers that all have <strong
                                    class="text-warning-400">{providerFamily.parent.name}</strong>
                                as
                                their parent provider.
                            </wa-tooltip>
                        </div>
                    </div>
                    <wa-scroller orientation="vertical" class="mb-3" style="max-height: 350px">
                        <FlexList defaultPageLength={5}
                                  {loading} data={providerFamily.siblings} {...list_props}></FlexList>
                    </wa-scroller>
                </div>
                <hr/>
                <div class="mb-4">
                    <div class="d-flex align-items-center mb-2 justify-content-between">
                        <div class="">
                            <h4 class="mb-0">Child Providers</h4>
                        </div>
                        <wa-icon class="help-icon" id="child-help" name="circle-question" variant="solid"></wa-icon>
                        <wa-tooltip for="child-help" placement="top-end">
                            Providers that have <strong class="text-warning-400">{providerFamily.self.name}</strong> as
                            a parent.
                        </wa-tooltip>
                    </div>
                    <wa-scroller orientation="vertical" style="max-height: 350px;">
                        <FlexList defaultPageLength={5}
                                  {loading} data={providerFamily.children} {...list_props}>
                        </FlexList>
                    </wa-scroller>
                </div>
            </div>
        {/key}
    </ModalBody>
    <ModalFooter class="justify-content-start">
        <button class="btn btn-primary rounded-pill btn-lg fs-6"
                onclick={toggle_modal}>
            Close
        </button>
    </ModalFooter>
</Modal>

<style>

    :root {
        --wa-tooltip-arrow-size: 2;
    }

    wa-scroller {
        --shadow-size: 1.5rem;
        --shadow-color: var(--hrst-secondary-50);
    }

    wa-icon.help-icon {
        display: inline-block;
        line-height: 0;
        height: 1.4rem;
        color: var(--hrst-error-500);
        font-size: 1.3rem;
        cursor: pointer;
    }
</style>